import { map, max } from 'lodash-es'
import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import { AppSectionSettings } from './settings-components/AppSection'
import { Checkin } from './settings-components/Checkin'
import { CryptoModule } from './settings-components/CryptoModule'
import { Editions } from './settings-components/Editions'
import { FestivalDates } from './settings-components/FestivalDates'
import { FooterManagement } from './settings-components/FooterManagement'
import { FriendsManagement } from './settings-components/FriendsManagement'
import { Genders } from './settings-components/Genders'
import { GeneralSettings } from './settings-components/GeneralSettings'
import { HeroSettingsComp } from './settings-components/HeroSettings'
import { ModuleManagement } from './settings-components/ModuleManagement'
import { PaymentMethods } from './settings-components/PaymenthMethods'
import { SocialLinks } from './settings-components/SocialLinks'
import { TicketsComp } from './settings-components/Tickets'
import { TopupModule } from './settings-components/Topup'
import Loader from '../../../common/components/Loader'
import { Days, Languages } from '../../../common/models'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import { getFestivalDays } from '../../../core/services/dates-settings'
import { getSettingsLanguages } from '../../../core/services/festival-settings'
import { useAppSelector } from '../../../store'
import { MessageBox } from '../messages/MessageBox'
import { Header, PageTitle } from '@/components'
import { Card, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui'

export const SettingsGeneralPage: React.FC = () => {
  const { festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name
  const editionId = storeFestivals
    .find((f) => f.platformName === festivalName)
    ?.editions.find((e) => e.active)?.id
  const [loading, setLoading] = useState<boolean>(false)
  const [allLanguages, setAllLanguages] = useState<Languages[]>([])
  const [days, setDays] = useState<Days[]>([])
  const [endTime, setEndTime] = useState<number>(0)
  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const { hasEditPermission } = useCheckPermission({ page: 'SETTINGS' })

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        setLoading(true)
        const allLanguagesResponse = await getSettingsLanguages()
        setAllLanguages(allLanguagesResponse.data)
        if (editionId) {
          const resp = await getFestivalDays(editionId)
          setDays(resp.data)
          const time = map(resp.data, d => d.endAt)
          setEndTime(max(time) || 0)
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }, [editionId, festivalName])

  return (
    <>
      <Header pageTitle="Settings" />
      {loading && <Loader />}
      <Card className="!bg-monochrome4-60 p-5 backdrop-blur-lg max-h-[calc(100vh-115px)] overflow-y-auto">
        <PageTitle title="Settings" subtitle="Festival Settings" />
        <Tabs defaultValue="general" className="w-full">
          <TabsList className="grid w-full grid-cols-4 lg:grid-cols-7 mt-4">
            <TabsTrigger value="general">General</TabsTrigger>
            <TabsTrigger value="modules">Modules</TabsTrigger>
            <TabsTrigger value="payment">Payment</TabsTrigger>
            <TabsTrigger value="dates">Dates & Events</TabsTrigger>
            <TabsTrigger value="content">Content</TabsTrigger>
            <TabsTrigger value="social">Social</TabsTrigger>
            <TabsTrigger value="appearance">Appearance</TabsTrigger>
          </TabsList>

          <TabsContent value="general" className="flex flex-col gap-4">
            <GeneralSettings
              hasEditPermission={hasEditPermission}
              allLanguages={allLanguages}
            />
            <Genders
              hasEditPermission={hasEditPermission}
              allLanguages={allLanguages}
            />
            <Editions hasEditPermission={hasEditPermission} />
          </TabsContent>

          <TabsContent value="modules" className="flex flex-col gap-4">
            <AppSectionSettings hasEditPermission={hasEditPermission} />
            <TicketsComp hasEditPermission={hasEditPermission} />
            <Checkin
              hasEditPermission={hasEditPermission}
              allLanguages={allLanguages}
            />
            <TopupModule
              hasEditPermission={hasEditPermission}
              endTime={endTime}
            />
            <ModuleManagement hasEditPermission={hasEditPermission} />
            <CryptoModule hasEditPermission={hasEditPermission} />
          </TabsContent>

          <TabsContent value="payment" className="flex flex-col gap-4">
            <PaymentMethods hasEditPermission={hasEditPermission} />
          </TabsContent>

          <TabsContent value="dates" className="flex flex-col gap-4">
            <FestivalDates
              days={days}
              allLanguages={allLanguages}
              setDays={setDays}
              hasEditPermission={hasEditPermission}
            />
          </TabsContent>

          <TabsContent value="content" className="flex flex-col gap-4">
            <HeroSettingsComp
              hasEditPermission={hasEditPermission}
              allLanguages={allLanguages}
            />
            <FooterManagement
              hasEditPermission={hasEditPermission}
              allLanguages={allLanguages}
            />
          </TabsContent>

          <TabsContent value="social" className="flex flex-col gap-4">
            <SocialLinks hasEditPermission={hasEditPermission} />
            <FriendsManagement hasEditPermission={hasEditPermission} />
          </TabsContent>

          <TabsContent value="appearance" className="flex flex-col gap-4">
            <HeroSettingsComp
              hasEditPermission={hasEditPermission}
              allLanguages={allLanguages}
            />
          </TabsContent>
        </Tabs>
      </Card>
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
